import React from "react";
import { useSelector } from 'react-redux';
import { checkDeviceLink } from '../Utils/DateFormatter';
import { checkBit, checkConnectionSlave,checkExistNA, parseBits, stateNA } from '../Utils/Functions';
import { calcCoordinate, generateArcPoints } from '../Utils/CalcCoordinate';
import { RS_COLOR } from "../Utils/Constants/ColorYandex";

const PrepareMapData = () => {
    const allDevices = useSelector((state) => state.dm?.data);
    const activeDevice = useSelector((state) => state.dmimei?.data[0]);
  
    // ВСЕ данные для отрисовки готовим здесь
    const mapData = React.useMemo(() => {
      if (!allDevices) return { dmCircles: [], rsCircles: [], polylines: [], polygons: [], mapCenter: null };
        // создаём пустые массивы
      const dmCircles = [];
      const polylines = [];
      const polygons = [];
      const mapObjects = [];
      const rsCircles = [];
      const rsLabels = [];
      
      const browserTime = new Date();

    allDevices.forEach((device, index) => {

        const center = [(device?.PositionCShr)*1, (device?.PositionCDl)*1]; // Центр круга
        const islink = checkDeviceLink(device?.param_last_date, browserTime);
        const isActive = activeDevice?.imei === device.imei; // Активная машина
  
        if (device?.type === 'секторная' || device?.type === 'круговая') {
          // ========== ПОДГОТОВКА КРУГОВ ДМ ==========
          const radius = device?.Ldm * 1;// Радиус дм
          const nowater = checkBit(device?.param4, 3);//включен режим "без воды"
          
          const circleData = {
            type: 'dm-circle',
            id: `dm-circle-${index}`,
            geometry: [center, radius],
            options: {
              draggable: false,
              fillColor: islink ? (nowater ? "rgba(37, 240, 14, 0.5)" : "rgba(40, 165, 237, 0.5)") : "#dedcdcb3",
              strokeColor: islink ? (nowater ? "rgba(35, 240, 12, 0.7)" : "rgba(22, 161, 242, 0.6)") : "#b8b4b4",
              strokeWidth: 2,
               zIndex: 5,
            },
            properties: {
              hintContent: device.imei_name,
              deviceId: device.imei,
            },
            isActive,
            clickable: true,
          };
          dmCircles.push(circleData);

           // ========== ЦЕНТРАЛЬНАЯ ТОЧКА ==========
           dmCircles.push({
            type: 'dm-center-point',
            id: `dm-center-${index}`,
            geometry: [center, radius * 0.05],
            options: {
              fillColor: '#ff0000',
              strokeColor: '#ff0000',
              strokeWidth: 2,
              zIndex: 5,
            },
            properties: {
              deviceId: device.imei,
            },
            isActive,
            clickable: true,
          });
  
          // ========== ПОДГОТОВКА ЛИНИЙ ДМ ==========
          const angleCurrent = device?.AngleCurrent * 1 + device?.OffsetNulN * 1;// Текущий угол
          const angleOffset = device?.OffsetNulN * 1; // смещение от севера
          
          // Линия текущего положения
          const currentPoint = calcCoordinate(center[0], center[1], radius, angleCurrent);
          polylines.push({
            type: 'dm-current-line',
            id: `dm-current-${index}`,
            geometry: [center, currentPoint],
            options: {
              strokeColor: '#ff0000',
              strokeWidth: 3,
              zIndex: 5,
            },
          });
  
          // Линия нулевого положения
          const offsetPoint = calcCoordinate(center[0], center[1], radius, angleOffset);
          polylines.push({
            type: 'dm-offset-line',
            id: `dm-offset-${index}`,
            geometry: [center, offsetPoint],
            options: {
              strokeColor: '#e8fc00',
              strokeWidth: 3,
              zIndex: 7,
            },
          });
  
          // Линии барьеров
          if (device?.FlagBarrier === 1) {
            if (device?.AngleForward !== 65535) {
              const angleBarrierF = device?.AngleForward * 1 + device?.OffsetNulN * 1;
              const barrierFPoint = calcCoordinate(center[0], center[1], radius + 20, angleBarrierF);
              polylines.push({
                type: 'dm-barrier-f',
                id: `dm-barrier-f-${index}`,
                geometry: [center, barrierFPoint],
                options: {
                  strokeColor: '#808080',
                  strokeWidth: 3,
                  zIndex: 5,
                },
              });
            }
  
            if (device?.AngleRevers !== 65535) {
              const angleBarrierR = device?.AngleRevers * 1 + device?.OffsetNulN * 1;
              const barrierRPoint = calcCoordinate(center[0], center[1], radius + 20, angleBarrierR);
              polylines.push({
                type: 'dm-barrier-r',
                id: `dm-barrier-r-${index}`,
                geometry: [center, barrierRPoint],
                options: {
                  strokeColor: '#808080',
                  strokeWidth: 3,
                  zIndex: 5,
                },
              });
  
              // Дуга разрешенной зоны
              const angleForward = device?.AngleForward * 1 + device?.OffsetNulN * 1;
              const angleRevers = device?.AngleRevers * 1 + device?.OffsetNulN * 1;
              const arcPoints = generateArcPoints(center, radius / 2, angleRevers, angleForward);
              
              if (arcPoints.length > 0) {
                polylines.push({
                  type: 'dm-arc',
                  id: `dm-arc-${index}`,
                  geometry: arcPoints,
                  options: {
                    strokeColor: 'rgba(128, 128, 128, 0.7)',
                    strokeWidth: 4,
                    zIndex: 5,
                  },
                });
              }
            }
          }
  
          // ========== ПОДГОТОВКА ТРЕУГОЛЬНИКОВ-СТРЕЛОК ==========
          const sizeMoveMarker = {
            angle: 8,
            size: radius * 0.2
          };
          
          const forward = checkBit(device?.param4, 6);
          const revers = checkBit(device?.param4, 4);
          
          let arrowheadAngle = forward ? (device?.AngleCurrent * 1 + sizeMoveMarker.angle + device?.OffsetNulN * 1) : (
            revers ? (device?.AngleCurrent * 1 - sizeMoveMarker.angle + device?.OffsetNulN * 1) : (device?.AngleCurrent * 1 + device?.OffsetNulN * 1)
          );

          const trianglePoint1 = calcCoordinate(center[0], center[1], radius, device?.AngleCurrent * 1 + device?.OffsetNulN * 1);
          const trianglePoint2 = calcCoordinate(center[0], center[1], radius - sizeMoveMarker.size / 2, arrowheadAngle);
          const trianglePoint3 = calcCoordinate(center[0], center[1], radius - sizeMoveMarker.size, device?.AngleCurrent * 1 + device?.OffsetNulN * 1);
         
          polygons.push({
            type: 'dm-triangle',
            id: `dm-triangle-${index}`,
            geometry: [trianglePoint1, trianglePoint2, trianglePoint3, trianglePoint1],
            options: {
              fillColor: islink ? "#ff0000" : "#dedcdcb3",
              fillOpacity: 0.5,
              strokeColor: islink ? "#ff0000" : "#dedcdcb3",
              strokeOpacity: 0.5,
              strokeWidth: 3,
              zIndex: 6,
            },
          });

        } else if (device?.type === 'RS485') {

          const isConnectionLost = checkConnectionSlave(device);
          const connectionColor = () => {
            if (!islink) return RS_COLOR.ISNOLINK;
            if (isConnectionLost) return RS_COLOR.ISNOCONNECT;
            return null; 
          };
          
          const colorProcesPch=() => {
            const colorPump = {
              fillColor: RS_COLOR.DEFAULT.fill,
              strokeColor: RS_COLOR.DEFAULT.stroke,
          };
            if (device?.typeRsDevice_name === 'PCH_Instart' ||
               device?.typeRsDevice_name === 'PCH_ESQ')  {
                const param6 = device?.param6;
                const isNoAlarm = device?.param12 === '65535' || device?.param12 === '0';

              if(param6 === '3' || param6 === '5')  {  // не запущен и нет аварий
                  if (isNoAlarm) {
                  colorPump.fillColor = RS_COLOR.READY.fill; //желтый
                  colorPump.strokeColor = RS_COLOR.READY.stroke;
                  }
              } else if(param6 === '1' || param6 === '2')  { //запущен и нет аварарий
                  if (isNoAlarm) {
                  colorPump.fillColor = RS_COLOR.RUN.fill; //зеленый
                  colorPump.strokeColor = RS_COLOR.RUN.stroke;
                  }
                }
              if (!isNoAlarm) { //есть аварии
                colorPump.fillColor = RS_COLOR.ALARM.fill; //красный
                colorPump.strokeColor = RS_COLOR.ALARM.stroke;
                }
            }
            if (device?.typeRsDevice_name === 'PCH_ATV') {
              const pchRedy = checkBit(device?.param6, 1);
              const pchRun = checkBit(device?.param6, 2);
              const isAlarm = checkBit(device?.param6, 3);

              if (pchRedy) {
                if (!isAlarm) {
                  colorPump.fillColor = RS_COLOR.READY.fill; //желтый
                  colorPump.strokeColor = RS_COLOR.READY.stroke;
                }
              } else if (pchRun) {
                if (!isAlarm) {
                  colorPump.fillColor = RS_COLOR.RUN.fill; //зеленый
                  colorPump.strokeColor = RS_COLOR.RUN.stroke;
                }
              }
              if (isAlarm) {
                colorPump.fillColor = RS_COLOR.ALARM.fill; //красный
                colorPump.strokeColor = RS_COLOR.ALARM.stroke;
              }
            }
            return colorPump;
          }; 

            // Обработка PLC устройств (до 4 насосов)
              const colorProcesPLC = () => {
                const colorPump = {
                  fillColor: RS_COLOR.DEFAULT.fill,
                  strokeColor: RS_COLOR.DEFAULT.stroke,
              };
              let hasAlarm = false;
              let hasRunningPump = false;
              let hasPumpData = false; // Флаг наличия данных по насосам

                for (let i = 1; i <= 4; i++) {
                    const paramIndex = (i - 1) * 3 + 5; //перебор 5,8,11,14
                    const existParam = device?.[`param${paramIndex}`];
                    
                    if (!checkExistNA(existParam)) continue;
                    hasPumpData=true;// Нашли данные хотя бы по одному насосу
   
                    const parsedBits = parseBits(parseInt(existParam));
                    const state = stateNA(parsedBits.bits0to3);
                    // Проверка на аварию (приоритетное условие)
                    if (state === 'Авария') {
                      hasAlarm = true;
                      break; // Прерываем цикл, так как достаточно одной аварии
                  }
                  // Проверка на запущенный насос
                  if (state === 'В работе') {
                      hasRunningPump = true;
                  } 
                }
                if (hasAlarm) {
                  colorPump.fillColor = RS_COLOR.ALARM.fill; //красный
                  colorPump.strokeColor = RS_COLOR.ALARM.stroke;
              } else if (hasRunningPump) {
                  colorPump.fillColor = RS_COLOR.RUN.fill;
                  colorPump.strokeColor = RS_COLOR.RUN.stroke;
              } else if (hasPumpData) {
                  colorPump.fillColor = RS_COLOR.READY.fill;
                  colorPump.strokeColor = RS_COLOR.READY.stroke;
              }
                 return colorPump;
            };

            const color = device?.typeRsDevice_name === 'PLC' 
            ? colorProcesPLC() 
            : ['PCH_Instart', 'PCH_ESQ', 'PCH_ATV'].includes(device?.typeRsDevice_name) 
            ? colorProcesPch() 
            : { fillColor: RS_COLOR.DEFAULT.fill, strokeColor: RS_COLOR.DEFAULT.stroke };

          // ========== ПОДГОТОВКА НАСОСНЫХ СТАНЦИЙ ==========
          const pumpData = {
            type: 'rs-pump',
            id: `rs-pump-${index}`,
            geometry: [center, 30], // Фиксированный радиус для насосной
            options: {
              draggable: false,
              fillColor: connectionColor()?.fill || color.fillColor || RS_COLOR.DEFAULT.fill,    
              strokeColor: connectionColor()?.stroke || color.strokeColor || RS_COLOR.DEFAULT.stroke,
              strokeWidth: 3,
              zIndex: 6,
            },
            properties: {
              hintContent: `${device.imei_name} (Насосная)`,
              deviceId: device.imei,
            },
            isActive,
            clickable: true,
            // Дополнительные данные для насосной
            pumpInfo: {
              pressure: device.pressure,
              status: device.status,
              // ...
            }
          };
          rsCircles.push(pumpData);

// кастомная надпись        

          const labelData = {
            type: 'label',
            id: `label-rs-pump-${index}`,
            geometry: center, // тот же центр, что у круга
            options: {
              // preset: 'islands#blackStretchyIcon',
              preset: 'islands#blackIcon',
              iconColor: 'transparent', // Делаем иконку прозрачной
              iconContentStyle: 'color: black; font-size: 12px;',
                      // iconContentStyle: `
                      //   color: black !important;
                      //   font-size: 12px !important;
                      //   font-family: Arial, sans-serif;
                      //   background: transparent !important;
                      //   padding: 0 !important;
                      //   margin: 0 !important;
                      // `,
                      // iconOffset: [-1, -3], 
              zIndex: 10,                   // выше круга (6 < 10)
            },
            properties: {
              iconContent: 'НC',  // сам текст
              hintContent: `${device.imei_name} (НС)`, // подсказка при наведении
            }
          };
          rsLabels.push(labelData); // сохраните в отдельный массив
        }
      });
  
      // ========== ОБВОДКА АКТИВНОГО УСТРОЙСТВА ==========
      if (activeDevice) {
        const activeCenter = [(activeDevice?.PositionCShr)*1, (activeDevice?.PositionCDl)*1];
        const activeRadius = activeDevice?.type === 'RS485'? 40
         : ((activeDevice?.Ldm*1 || 50) + 50);
        
        mapObjects.push({
          type: 'active-outline',
          id: 'active-outline',
          geometry: [activeCenter, activeRadius],
          options: {
            fillColor: "rgba(255, 255, 255, 0)",
            strokeColor: "rgba(255, 162, 3, 1)",
            strokeWidth: 5,
            zIndex: 3,
          },
        });
      }
  
      // ========== ЦЕНТР КАРТЫ ==========
      const mapCenter = activeDevice 
        ? [(activeDevice?.PositionCShr)*1, (activeDevice?.PositionCDl)*1]
        : allDevices.length > 0 
          ? [(allDevices[0]?.PositionCShr)*1, (allDevices[0]?.PositionCDl)*1]
          : [55.751244, 37.618423];
      const zoom = activeDevice?.type === 'RS485'? 17 : 13;
      return {
        dmCircles,
        rsCircles,
        polylines,
        polygons,
        mapObjects,
        mapCenter,
        rsLabels,
        zoom: zoom
      };
    }, [allDevices, activeDevice]);

    return mapData;
};
export default PrepareMapData;
